import { Form, SubmitButton, Text, TextInputField } from '@/components';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { useRequestOneTimeCode } from '../_hooks/reset-password';
import { RequestOneTimeCodeFormSchema } from '../_models/reset-password';

type FormValues = z.infer<typeof RequestOneTimeCodeFormSchema>;

type Props = {
  setTargetUserKey: Dispatch<SetStateAction<string | null>>;
  setTargetEmailAddress: Dispatch<SetStateAction<string | null>>;
};

export const RequestOneTimeCodeForm: FC<Props> = ({
  setTargetUserKey,
  setTargetEmailAddress,
}) => {
  const { t } = useTranslation();
  const { mutateAsync: requestOneTimeCodeAsync } = useRequestOneTimeCode();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting, isSubmitSuccessful },
  } = useForm<FormValues>({
    resolver: zodResolver(RequestOneTimeCodeFormSchema),
  });

  const onSubmit = async (
    values: z.infer<typeof RequestOneTimeCodeFormSchema>,
  ) => {
    const res = await requestOneTimeCodeAsync(values);
    setTargetUserKey(res.key);
    setTargetEmailAddress(values.email);
  };

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <Form.Item>
        <Text text={t('input-email-address-to-send-one-time-code')} />
      </Form.Item>

      <TextInputField
        label={t('email')}
        name="email"
        control={control}
        errors={errors}
        fallbackValue={null}
      />

      <Form.Item>
        <SubmitButton
          text={t('send')}
          disabled={!isDirty}
          loading={isSubmitting}
        />
      </Form.Item>
    </Form>
  );
};
